import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Search, Calendar, CalendarDays, FileText, CheckCircle, Edit, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { paymentSchedulesApi, clientsApi, projectsApi } from '../lib/api';
import { formatCurrency, formatDate, cn } from '../lib/utils';
import { DataTable } from '../components/ui/data-table';

const MONTHS_RU = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
];

const STATUS_CONFIG = {
  planned:  { label: 'Запланирован', bg: 'bg-blue-50',    border: 'border-l-blue-500',   badge: 'bg-blue-100 text-blue-800' },
  invoiced: { label: 'Счёт выставлен', bg: 'bg-amber-50', border: 'border-l-amber-500',  badge: 'bg-amber-100 text-amber-800' },
  paid:     { label: 'Оплачен',     bg: 'bg-emerald-50',  border: 'border-l-emerald-500', badge: 'bg-emerald-100 text-emerald-800' },
  overdue:  { label: 'Просрочен',   bg: 'bg-red-50',      border: 'border-l-red-500',     badge: 'bg-red-100 text-red-800' },
};

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

function getCalendarDays(month, year) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  let startDow = firstDay.getDay() - 1;
  if (startDow < 0) startDow = 6;

  const days = [];
  const prevLast = new Date(year, month, 0).getDate();
  for (let i = startDow - 1; i >= 0; i--) {
    days.push({ day: prevLast - i, current: false });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    days.push({ day: d, current: true });
  }
  const rem = 7 - (days.length % 7);
  if (rem < 7) {
    for (let d = 1; d <= rem; d++) {
      days.push({ day: d, current: false });
    }
  }
  return days;
}

function CalendarView({ calMonth, calYear, setCalMonth, setCalYear, schedules, clients, isLoading, onScheduleClick }) {
  const today = new Date();
  const todayDay = today.getDate();
  const todayMonth = today.getMonth();
  const todayYear = today.getFullYear();

  const days = getCalendarDays(calMonth, calYear);

  const prevMonth = () => {
    if (calMonth === 0) { setCalMonth(11); setCalYear(calYear - 1); }
    else setCalMonth(calMonth - 1);
  };
  const nextMonth = () => {
    if (calMonth === 11) { setCalMonth(0); setCalYear(calYear + 1); }
    else setCalMonth(calMonth + 1);
  };
  const goToday = () => { setCalMonth(today.getMonth()); setCalYear(today.getFullYear()); };

  const byDay = {};
  (schedules || []).forEach(s => {
    if (!s.scheduled_date) return;
    const d = new Date(s.scheduled_date);
    if (d.getMonth() === calMonth && d.getFullYear() === calYear) {
      const key = d.getDate();
      if (!byDay[key]) byDay[key] = [];
      byDay[key].push(s);
    }
  });

  return (
    <div className="bg-white rounded-xl border overflow-hidden" data-testid="calendar-view">
      {/* Calendar header */}
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center gap-3">
          <button onClick={prevMonth} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors" data-testid="cal-prev">
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <h3 className="text-base font-semibold text-gray-900 min-w-[180px] text-center">
            {MONTHS_RU[calMonth]} {calYear}
          </h3>
          <button onClick={nextMonth} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors" data-testid="cal-next">
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        </div>
        <button onClick={goToday} className="px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg border border-blue-200 transition-colors" data-testid="cal-today">
          Сегодня
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full" />
        </div>
      ) : (
        <div className="p-2">
          {/* Weekday headers */}
          <div className="grid grid-cols-7 mb-1">
            {WEEKDAYS.map(wd => (
              <div key={wd} className="text-center text-xs font-semibold text-gray-400 uppercase py-2">{wd}</div>
            ))}
          </div>
          {/* Day cells */}
          <div className="grid grid-cols-7 gap-px bg-gray-100 rounded-lg overflow-hidden">
            {days.map((cell, idx) => {
              const isToday = cell.current && cell.day === todayDay && calMonth === todayMonth && calYear === todayYear;
              const daySchedules = cell.current ? (byDay[cell.day] || []) : [];
              return (
                <div key={idx}
                  className={cn(
                    'bg-white min-h-[100px] p-1.5 flex flex-col transition-colors',
                    !cell.current && 'bg-gray-50/60',
                    isToday && 'ring-2 ring-inset ring-blue-400',
                  )}
                  data-testid={cell.current ? `cal-day-${cell.day}` : undefined}
                >
                  <span className={cn(
                    'text-xs font-medium mb-1 w-6 h-6 flex items-center justify-center rounded-full',
                    !cell.current && 'text-gray-300',
                    cell.current && 'text-gray-700',
                    isToday && 'bg-blue-600 text-white',
                  )}>
                    {cell.day}
                  </span>
                  <div className="flex flex-col gap-0.5 overflow-y-auto max-h-[80px]">
                    {daySchedules.map(s => {
                      const cfg = STATUS_CONFIG[s.status] || STATUS_CONFIG.planned;
                      const client = clients.find(c => c.id === s.client_id);
                      return (
                        <button key={s.id}
                          onClick={() => onScheduleClick(s)}
                          className={cn(
                            'w-full text-left px-1.5 py-1 rounded text-[11px] leading-tight border-l-2 truncate transition-colors hover:opacity-80',
                            cfg.bg, cfg.border,
                          )}
                          title={`${client?.name || ''}: ${s.description || ''} — ${formatCurrency(s.amount)}`}
                          data-testid={`cal-schedule-${s.id}`}
                        >
                          <span className="font-semibold">{formatCurrency(s.amount)}</span>
                          {client && <span className="text-gray-500 ml-1">{client.name.length > 12 ? client.name.slice(0, 12) + '…' : client.name}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function ScheduleDialog({ isOpen, onClose, schedule, clients, projects }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    client_id: '', amount: '', scheduled_date: '', description: '', notes: '',
    project_id: '', recurring: false, recurring_interval: '',
  });

  React.useEffect(() => {
    if (isOpen) {
      if (schedule) {
        setForm({
          client_id: schedule.client_id || '',
          project_id: schedule.project_id || '',
          amount: schedule.amount?.toString() || '',
          scheduled_date: schedule.scheduled_date ? new Date(schedule.scheduled_date).toISOString().split('T')[0] : '',
          description: schedule.description || '',
          notes: schedule.notes || '',
          recurring: schedule.recurring || false,
          recurring_interval: schedule.recurring_interval || '',
        });
      } else {
        setForm({ client_id: '', amount: '', scheduled_date: '', description: '', notes: '', project_id: '', recurring: false, recurring_interval: '' });
      }
    }
  }, [isOpen, schedule]);

  const mutation = useMutation({
    mutationFn: (data) => schedule
      ? paymentSchedulesApi.update(schedule.id, data)
      : paymentSchedulesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-schedules'] });
      queryClient.invalidateQueries({ queryKey: ['schedule-summary'] });
      toast.success(schedule ? 'Запись обновлена' : 'Платёж запланирован');
      onClose();
    },
    onError: () => toast.error('Ошибка сохранения'),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.client_id || !form.amount || !form.scheduled_date) {
      toast.error('Заполните обязательные поля');
      return;
    }
    mutation.mutate({
      ...form,
      amount: parseFloat(form.amount),
      project_id: form.project_id || null,
      recurring_interval: form.recurring ? form.recurring_interval : null,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-semibold text-gray-900">
            {schedule ? 'Редактировать' : 'Запланировать платёж'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Клиент *</label>
              <select value={form.client_id} onChange={e => setForm({ ...form, client_id: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500" data-testid="schedule-client-select">
                <option value="">Выберите клиента</option>
                {clients?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Проект</label>
              <select value={form.project_id} onChange={e => setForm({ ...form, project_id: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500" data-testid="schedule-project-select">
                <option value="">Без проекта</option>
                {projects?.filter(p => !form.client_id || p.client_id === form.client_id).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Сумма (руб.) *</label>
              <input type="number" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500" data-testid="schedule-amount-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Дата платежа *</label>
              <input type="date" value={form.scheduled_date} onChange={e => setForm({ ...form, scheduled_date: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500" data-testid="schedule-date-input" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Описание</label>
            <input type="text" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Например: Ежемесячная оплата за SEO" data-testid="schedule-description-input" />
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.recurring} onChange={e => setForm({ ...form, recurring: e.target.checked })}
                className="rounded border-gray-300" />
              Повторяющийся
            </label>
            {form.recurring && (
              <select value={form.recurring_interval} onChange={e => setForm({ ...form, recurring_interval: e.target.value })}
                className="px-3 py-1.5 border rounded-lg text-sm">
                <option value="monthly">Ежемесячно</option>
                <option value="quarterly">Ежеквартально</option>
              </select>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Примечания</label>
            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2}
              className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="schedule-notes-input" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50">Отменить</button>
            <button type="submit" disabled={mutation.isPending}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50" data-testid="schedule-submit-btn">
              {mutation.isPending ? 'Сохранение...' : schedule ? 'Сохранить' : 'Запланировать'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function PaymentSchedules() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [calYear, setCalYear] = useState(new Date().getFullYear());

  const queryMonth = viewMode === 'calendar' ? calMonth + 1 : selectedMonth;
  const queryYear = viewMode === 'calendar' ? calYear : (selectedMonth ? selectedYear : undefined);

  const { data: schedulesData, isLoading } = useQuery({
    queryKey: ['payment-schedules', searchTerm, statusFilter, clientFilter, queryMonth, queryYear, viewMode],
    queryFn: () => paymentSchedulesApi.getAll({
      search: searchTerm || undefined,
      status: statusFilter || undefined,
      client_id: clientFilter || undefined,
      month: queryMonth || undefined,
      year: queryYear || undefined,
    }).then(r => r.data),
  });

  const { data: summaryData } = useQuery({
    queryKey: ['schedule-summary', selectedYear],
    queryFn: () => paymentSchedulesApi.getSummary(selectedYear).then(r => r.data),
  });

  const { data: clientsData } = useQuery({
    queryKey: ['clients-list'],
    queryFn: () => clientsApi.getAll({ limit: 100 }).then(r => r.data),
  });

  const { data: projectsData } = useQuery({
    queryKey: ['projects-list-schedules'],
    queryFn: () => projectsApi.getAll({ limit: 100 }).then(r => r.data),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => paymentSchedulesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-schedules'] });
      queryClient.invalidateQueries({ queryKey: ['schedule-summary'] });
      toast.success('Запись удалена');
    },
  });

  const createInvoiceMutation = useMutation({
    mutationFn: (id) => paymentSchedulesApi.createInvoice(id),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['payment-schedules'] });
      queryClient.invalidateQueries({ queryKey: ['schedule-summary'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      toast.success(`Счёт ${res.data.invoice_number} создан`);
    },
    onError: () => toast.error('Ошибка создания счёта'),
  });

  const clients = clientsData?.clients || [];
  const projects = projectsData?.projects || [];
  const schedules = schedulesData?.schedules || [];
  const months = summaryData?.months || [];

  const columns = [
    {
      header: 'Дата',
      accessor: 'scheduled_date',
      cell: (s) => {
        const cfg = STATUS_CONFIG[s.status] || STATUS_CONFIG.planned;
        return (
          <div className={`flex items-center gap-2 pl-1 border-l-4 ${cfg.border}`}>
            <CalendarDays className="w-4 h-4 text-gray-500 flex-shrink-0" />
            <span className="text-sm font-medium text-gray-900">{formatDate(s.scheduled_date)}</span>
          </div>
        );
      },
    },
    {
      header: 'Клиент',
      accessor: 'client_id',
      cell: (s) => {
        const client = clients.find(c => c.id === s.client_id);
        return <span className="text-sm text-gray-900">{client?.name || '—'}</span>;
      },
    },
    {
      header: 'Описание',
      accessor: 'description',
      cell: (s) => (
        <div>
          <p className="text-sm text-gray-900">{s.description || '—'}</p>
          {s.recurring && (
            <span className="text-xs text-purple-600">
              {s.recurring_interval === 'monthly' ? 'Ежемесячно' : 'Ежеквартально'}
            </span>
          )}
        </div>
      ),
    },
    {
      header: 'Сумма',
      accessor: 'amount',
      cell: (s) => <span className="text-sm font-semibold text-gray-900">{formatCurrency(s.amount)}</span>,
    },
    {
      header: 'Статус',
      accessor: 'status',
      cell: (s) => {
        const cfg = STATUS_CONFIG[s.status] || STATUS_CONFIG.planned;
        return (
          <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${cfg.badge}`} data-testid={`schedule-status-${s.id}`}>
            {cfg.label}
          </span>
        );
      },
    },
    {
      header: 'Действия',
      accessor: 'actions',
      cell: (s) => (
        <div className="flex items-center gap-1.5">
          {s.status === 'planned' && (
            <button onClick={e => { e.stopPropagation(); createInvoiceMutation.mutate(s.id); }}
              className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-md transition-colors" title="Выставить счёт"
              data-testid={`create-invoice-${s.id}`} disabled={createInvoiceMutation.isPending}>
              <FileText className="w-4 h-4" />
            </button>
          )}
          {s.status === 'planned' && (
            <button onClick={e => { e.stopPropagation(); setSelectedSchedule(s); setIsDialogOpen(true); }}
              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md transition-colors" title="Редактировать"
              data-testid={`edit-schedule-${s.id}`}>
              <Edit className="w-4 h-4" />
            </button>
          )}
          <button onClick={e => { e.stopPropagation(); if (confirm('Удалить запись?')) deleteMutation.mutate(s.id); }}
            className="p-1.5 text-red-600 hover:bg-red-50 rounded-md transition-colors" title="Удалить"
            data-testid={`delete-schedule-${s.id}`}>
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  // Color-coded row background
  const getRowClass = (s) => {
    const cfg = STATUS_CONFIG[s.status];
    return cfg ? cfg.bg : '';
  };

  return (
    <div className="space-y-6" data-testid="payment-schedules-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">График платежей</h1>
          <p className="text-sm text-gray-500">Запланированные платежи по клиентам ({schedulesData?.total || 0})</p>
        </div>
        <div className="flex items-center gap-3">
          {/* View toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-0.5" data-testid="view-toggle">
            <button onClick={() => setViewMode('table')}
              className={cn('flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                viewMode === 'table' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700')}
              data-testid="view-table-btn">
              <Search className="w-3.5 h-3.5" /> Таблица
            </button>
            <button onClick={() => setViewMode('calendar')}
              className={cn('flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                viewMode === 'calendar' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700')}
              data-testid="view-calendar-btn">
              <Calendar className="w-3.5 h-3.5" /> Календарь
            </button>
          </div>
          <button onClick={() => { setSelectedSchedule(null); setIsDialogOpen(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors"
            data-testid="add-schedule-btn">
            <Plus className="w-4 h-4" /> Запланировать платёж
          </button>
        </div>
      </div>

      {/* Monthly summary cards */}
      <div className="bg-white rounded-xl border p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-700">Сводка по месяцам</h3>
          <div className="flex items-center gap-2">
            <button onClick={() => setSelectedYear(y => y - 1)} className="p-1 hover:bg-gray-100 rounded">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm font-medium w-12 text-center">{selectedYear}</span>
            <button onClick={() => setSelectedYear(y => y + 1)} className="p-1 hover:bg-gray-100 rounded">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="grid grid-cols-6 lg:grid-cols-12 gap-1.5">
          {months.map((m, i) => {
            const isSelected = selectedMonth === m.month;
            const hasData = m.total_amount > 0;
            const hasOverdue = m.overdue.count > 0;
            const allPaid = hasData && m.paid.count > 0 && m.planned.count === 0 && m.overdue.count === 0;

            return (
              <button key={i}
                onClick={() => setSelectedMonth(isSelected ? null : m.month)}
                className={cn(
                  'flex flex-col items-center p-2 rounded-lg text-xs transition-all border',
                  isSelected ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-200' : 'border-transparent hover:bg-gray-50',
                  hasOverdue && !isSelected ? 'bg-red-50' : '',
                  allPaid && !isSelected ? 'bg-emerald-50' : '',
                )}
                data-testid={`month-btn-${m.month}`}
              >
                <span className="font-medium text-gray-600 mb-0.5">{MONTHS_RU[i].slice(0, 3)}</span>
                {hasData ? (
                  <span className={cn(
                    'font-bold text-[11px]',
                    hasOverdue ? 'text-red-700' : allPaid ? 'text-emerald-700' : 'text-gray-900'
                  )}>
                    {(m.total_amount / 1000).toFixed(0)}K
                  </span>
                ) : (
                  <span className="text-gray-300">—</span>
                )}
                {hasData && (
                  <div className="flex gap-0.5 mt-0.5">
                    {m.planned.count > 0 && <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />}
                    {m.invoiced.count > 0 && <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />}
                    {m.paid.count > 0 && <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />}
                    {m.overdue.count > 0 && <div className="w-1.5 h-1.5 rounded-full bg-red-500" />}
                  </div>
                )}
              </button>
            );
          })}
        </div>
        {/* Legend */}
        <div className="flex items-center gap-4 mt-3 pt-3 border-t text-xs text-gray-500">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" /> Запланирован</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> Счёт выставлен</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Оплачен</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> Просрочен</span>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" placeholder="Поиск по описанию..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm" data-testid="schedule-search-input" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-2 border rounded-lg text-sm" data-testid="schedule-status-filter">
          <option value="">Все статусы</option>
          <option value="planned">Запланирован</option>
          <option value="invoiced">Счёт выставлен</option>
          <option value="paid">Оплачен</option>
          <option value="overdue">Просрочен</option>
        </select>
        <select value={clientFilter} onChange={e => setClientFilter(e.target.value)}
          className="px-3 py-2 border rounded-lg text-sm" data-testid="schedule-client-filter">
          <option value="">Все клиенты</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {selectedMonth && (
          <button onClick={() => setSelectedMonth(null)}
            className="px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg border border-blue-200">
            {MONTHS_RU[selectedMonth - 1]} &times;
          </button>
        )}
      </div>

      {/* Content: Table or Calendar */}
      {viewMode === 'table' ? (
        <div className="bg-white rounded-xl border overflow-hidden" data-testid="table-view">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full" />
            </div>
          ) : schedules.length === 0 ? (
            <div className="text-center py-16">
              <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 mb-1">Нет запланированных платежей</p>
              <p className="text-sm text-gray-400">Нажмите "Запланировать платёж" чтобы добавить</p>
            </div>
          ) : (
            <table className="w-full" data-testid="schedules-table">
              <thead>
                <tr className="border-b bg-gray-50">
                  {columns.map((col, i) => (
                    <th key={i} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {col.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {schedules.map(s => (
                  <tr key={s.id} className={cn('hover:bg-gray-50 transition-colors cursor-pointer', getRowClass(s))}
                    onClick={() => { setSelectedSchedule(s); setIsDialogOpen(true); }} data-testid={`schedule-row-${s.id}`}>
                    {columns.map((col, i) => (
                      <td key={i} className="px-4 py-3">{col.cell(s)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        <CalendarView
          calMonth={calMonth}
          calYear={calYear}
          setCalMonth={setCalMonth}
          setCalYear={setCalYear}
          schedules={schedules}
          clients={clients}
          isLoading={isLoading}
          onScheduleClick={(s) => { setSelectedSchedule(s); setIsDialogOpen(true); }}
        />
      )}

      <ScheduleDialog
        isOpen={isDialogOpen}
        onClose={() => { setIsDialogOpen(false); setSelectedSchedule(null); }}
        schedule={selectedSchedule}
        clients={clients}
        projects={projects}
      />
    </div>
  );
}
